import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { apiService } from '../../services/apiService';

interface Customer {
  id: number;
  company_name: string;
  contact_person: string;
}

const EditInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [formData, setFormData] = useState({
    invoice_number: '',
    customer_id: '',
    invoice_date: '',
    due_date: '',
    subtotal: '',
    tax_amount: '',
    discount_amount: '',
    total_amount: '',
    status: 'draft',
    notes: '',
    terms_conditions: '',
    payment_method: '',
    payment_date: '',
    billing_address: '',
    shipping_address: ''
  });

  useEffect(() => {
    if (id) {
      fetchInvoiceData();
      fetchDropdownData();
    }
  }, [id]);

  const fetchInvoiceData = async () => {
    try {
      setFetching(true);
      const response = await apiService.getInvoice(parseInt(id!));
      const invoice = response.data;
      
      console.log('Fetched invoice data:', invoice);
      
      setFormData({
        invoice_number: invoice.invoice_number || '',
        customer_id: invoice.customer_id?.toString() || '',
        invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : '',
        due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
        subtotal: invoice.subtotal?.toString() || '',
        tax_amount: invoice.tax_amount?.toString() || '',
        discount_amount: invoice.discount_amount?.toString() || '',
        total_amount: invoice.total_amount?.toString() || '',
        status: invoice.status || 'draft',
        notes: invoice.notes || '',
        terms_conditions: invoice.terms_conditions || '',
        payment_method: invoice.payment_method || '',
        payment_date: invoice.payment_date ? invoice.payment_date.split('T')[0] : '',
        billing_address: invoice.billing_address || '',
        shipping_address: invoice.shipping_address || ''
      });
    } catch (error: any) {
      console.error('Failed to fetch invoice data:', error);
      toast.error(error.message || 'Failed to fetch invoice data');
      navigate('/invoices');
    } finally {
      setFetching(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const response = await apiService.getInvoiceDropdownData();
      setCustomers(response.data.customers || []);
    } catch (error: any) {
      console.error('Failed to fetch dropdown data:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };

      // Auto-calculate total amount when subtotal, tax, or discount changes
      if (['subtotal', 'tax_amount', 'discount_amount'].includes(field)) {
        const subtotal = field === 'subtotal' ? parseFloat(value) || 0 : parseFloat(updatedData.subtotal) || 0;
        const taxAmount = field === 'tax_amount' ? parseFloat(value) || 0 : parseFloat(updatedData.tax_amount) || 0;
        const discountAmount = field === 'discount_amount' ? parseFloat(value) || 0 : parseFloat(updatedData.discount_amount) || 0;
        
        const totalAmount = subtotal + taxAmount - discountAmount;
        
        return {
          ...updatedData,
          total_amount: totalAmount.toString()
        };
      }

      return updatedData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Convert string values to appropriate types
      const submitData = {
        ...formData,
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : undefined,
        subtotal: parseFloat(formData.subtotal) || 0,
        tax_amount: parseFloat(formData.tax_amount) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        total_amount: parseFloat(formData.total_amount) || 0,
        status: formData.status as 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'
      };

      console.log('Submitting invoice data:', submitData);
      
      const response = await apiService.updateInvoice(parseInt(id!), submitData);
      console.log('Update response:', response);
      
      toast.success('Invoice updated successfully!');
      navigate('/invoices');
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      
      if (error.response && error.response.data && error.response.data.errors) {
        // Handle validation errors
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat();
        toast.error(errorMessages.join(', '));
      } else {
        toast.error(error.message || 'Failed to update invoice');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/invoices');
  };

  if (fetching) {
    return (
      <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', fontSize: '2rem' }}>
          Edit Invoice
        </Typography>
      </Box>

      <Card sx={{ 
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
        borderRadius: '16px',
        backgroundColor: 'white',
        border: 'none'
      }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            {/* Invoice Details Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Invoice Details
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Invoice Number"
                  value={formData.invoice_number}
                  onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={formData.customer_id || ''}
                    onChange={(e) => handleInputChange('customer_id', e.target.value)}
                    label="Customer"
                  >
                    {customers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id.toString()}>
                        {customer.company_name} - {customer.contact_person}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Invoice Date"
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) => handleInputChange('invoice_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => handleInputChange('due_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Amount Details Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Amount Details
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Subtotal"
                  type="number"
                  value={formData.subtotal}
                  onChange={(e) => handleInputChange('subtotal', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Tax Amount"
                  type="number"
                  value={formData.tax_amount}
                  onChange={(e) => handleInputChange('tax_amount', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Discount Amount"
                  type="number"
                  value={formData.discount_amount}
                  onChange={(e) => handleInputChange('discount_amount', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Total Amount"
                  type="number"
                  value={formData.total_amount}
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      backgroundColor: '#f8fafc',
                      color: '#374151',
                      fontWeight: 'bold'
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Payment Details Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Payment Details
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="sent">Sent</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Payment Method"
                  value={formData.payment_method}
                  onChange={(e) => handleInputChange('payment_method', e.target.value)}
                  placeholder="Enter"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Payment Date"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => handleInputChange('payment_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Address Details Section */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 3 }}>
              Address Details
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Billing Address"
                  multiline
                  rows={3}
                  value={formData.billing_address}
                  onChange={(e) => handleInputChange('billing_address', e.target.value)}
                  placeholder="Enter billing address..."
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Shipping Address"
                  multiline
                  rows={3}
                  value={formData.shipping_address}
                  onChange={(e) => handleInputChange('shipping_address', e.target.value)}
                  placeholder="Enter shipping address..."
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Terms & Conditions"
                  multiline
                  rows={3}
                  value={formData.terms_conditions}
                  onChange={(e) => handleInputChange('terms_conditions', e.target.value)}
                  placeholder="Enter terms and conditions..."
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Enter notes..."
                  variant="outlined"
                />
              </Grid>
            </Grid>

            {/* Form Actions */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 3,
              borderTop: '1px solid #e2e8f0'
            }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    backgroundColor: '#f8fafc',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={<SaveIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#3b82f6',
                  },
                }}
              >
                {loading ? 'Updating...' : 'Update Invoice'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditInvoice;
